import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  Chip,
  IconButton,
  CircularProgress,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Publish as PublishIcon,
  TrendingUp as TrendingUpIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { supabase } from '../../services/supabase';
import { backendApiService } from '../../services/backendApiService';

// Declare confetti function type
declare global {
  interface Window {
    confetti: (options?: {
      particleCount?: number;
      spread?: number;
      origin?: { x?: number; y?: number };
    }) => void;
  }
}

interface ContentProject {
  id: string;
  website_id: string;
  project_name: string;
  primary_keyword: string;
  status: string;
  notes: string;
  target_url: string;
}

interface ProjectKeyword {
  id: string;
  keyword: string;
  search_volume: number;
  is_primary: boolean;
  estimated_difficulty: number;
  language_code?: string;
  country_code?: string;
}

interface CompetitorAnalysis {
  id: string;
  competitor_url: string;
  competitor_domain: string;
  ranking_position: number;
  title: string;
  meta_description: string;
  word_count: number;
  research_country?: string;
  competitor_type?: string;
}

interface FAQResearch {
  id: string;
  question: string;
  answer: string;
  source_url: string;
  relevance_score: number;
  parent_question?: string;
  depth_level?: number;
  position_in_level?: number;
}

// Country configuration matching your websites
const COUNTRY_CONFIG = [
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧', website: 'newyork.co.uk' },
  { code: 'COM_US', name: 'United States', flag: '🇺🇸', website: 'nuevayork.com', group: 'Americas' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', website: 'newyork.nl' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', website: 'newyorkcity.de' },
  { code: 'FR', name: 'France', flag: '🇫🇷', website: 'newyorkcity.fr' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', website: 'nuevayork.es' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', website: 'newyorkcity.it' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', website: 'newyork.se' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', website: 'newyork.no' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', website: 'newyorkcity.dk' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', website: 'newyork.fi' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', website: 'newyork.jp' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', website: 'newyorkcity.ca' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', website: 'newyork.com.au' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', website: 'newyork.kr' },
  // Spanish-speaking countries group with nuevayork.com
  { code: 'COM_MX', name: 'Mexico', flag: '🇲🇽', website: 'nuevayork.com', group: 'Americas' },
  { code: 'COM_AR', name: 'Argentina', flag: '🇦🇷', website: 'nuevayork.com', group: 'Americas' },
  { code: 'COM_CL', name: 'Chile', flag: '🇨🇱', website: 'nuevayork.com', group: 'Americas' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`research-tabpanel-${index}`}
      aria-labelledby={`research-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const ProjectResearchHub: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ContentProject | null>(null);
  const [keywords, setKeywords] = useState<ProjectKeyword[]>([]);
  const [competitors, setCompetitors] = useState<CompetitorAnalysis[]>([]);
  const [faqs, setFaqs] = useState<FAQResearch[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newKeyword, setNewKeyword] = useState('');
  const [publishUrl, setPublishUrl] = useState('');
  const [showAddKeywordDialog, setShowAddKeywordDialog] = useState(false);
  const [researchingKeyword, setResearchingKeyword] = useState<string | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<string[]>(['UK']);
  const [gettingRelated, setGettingRelated] = useState(false);
  const [fetchingPAA, setFetchingPAA] = useState(false);
  const [researchProgress, setResearchProgress] = useState<string>('');
  const [paaDepth, setPaaDepth] = useState<number>(2);
  const [showTranslationDialog, setShowTranslationDialog] = useState(false);
  const [keywordTranslations, setKeywordTranslations] = useState<Record<string, string>>({});
  const [pendingResearchKeyword, setPendingResearchKeyword] = useState<string>('');
  const [languageTabValue, setLanguageTabValue] = useState(0);
  const [competitorTabValue, setCompetitorTabValue] = useState(0);

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    }
  }, [projectId]);

  // Reset sub-tab values when switching main tabs
  useEffect(() => {
    if (tabValue === 0) {
      setLanguageTabValue(0);
    } else if (tabValue === 1) {
      setCompetitorTabValue(0);
    }
  }, [tabValue]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      // Fetch project details
      const { data: projectData, error: projectError } = await (supabase as any)
        .from('content_projects')
        .select('*')
        .eq('id', projectId)
        .single();
      
      if (projectError) throw projectError;
      setProject(projectData);
      setPublishUrl(projectData.target_url || '');

      // Fetch keywords
      const { data: keywordsData } = await (supabase as any)
        .from('project_keywords')
        .select('*')
        .eq('project_id', projectId)
        .order('is_primary', { ascending: false });
      setKeywords(keywordsData || []);

      // Fetch competitors via API endpoint
      const competitorsResponse = await backendApiService.get(`/api/project-competitors/${projectId}`);
      const competitorsData = competitorsResponse.success ? competitorsResponse.data : [];
      setCompetitors(competitorsData || []);

      // Fetch FAQs
      const { data: faqsData } = await (supabase as any)
        .from('project_faq_research')
        .select('*')
        .eq('project_id', projectId)
        .order('relevance_score', { ascending: false });
      setFaqs(faqsData || []);
    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addKeyword = async () => {
    if (!newKeyword.trim() || !projectId) return;

    try {
      const { error } = await (supabase as any)
        .from('project_keywords')
        .insert([{
          project_id: projectId,
          keyword: newKeyword.trim(),
          is_primary: keywords.length === 0,
          language_code: 'en',
          country_code: 'en'
        }]);
      
      if (error) throw error;
      setNewKeyword('');
      setShowAddKeywordDialog(false);
      fetchProjectData();
    } catch (error) {
      console.error('Error adding keyword:', error);
      alert('Failed to add keyword. Please try again.');
    }
  };

  const getRelatedKeywords = async () => {
    if (keywords.length === 0) return;
    
    setGettingRelated(true);
    try {
      // Use primary keyword or first keyword as seed
      const seedKeyword = keywords.find(k => k.is_primary)?.keyword || keywords[0].keyword;
      
      const response = await backendApiService.post('/api/keywords/ideas', {
        seedKeywords: [seedKeyword],
        country: selectedCountries[0] || 'UK',
        language: 'en'
      });
      
      if (response.keywords && response.keywords.length > 0) {
        // Filter out keywords we already have
        const existingKeywords = new Set(keywords.map(k => k.keyword.toLowerCase()));
        const newKeywords = response.keywords
          .filter((k: any) => !existingKeywords.has(k.keyword.toLowerCase()))
          .slice(0, 20); // Limit to 20 suggestions
        
        // Add new keywords to the project
        for (const kw of newKeywords) {
          await (supabase as any)
            .from('project_keywords')
            .insert([{
              project_id: projectId,
              keyword: kw.keyword,
              search_volume: kw.avgMonthlySearches,
              is_primary: false
            }]);
        }
        
        fetchProjectData();
      }
    } catch (error) {
      console.error('Error getting related keywords:', error);
      alert('Error getting related keywords. Please check the console.');
    } finally {
      setGettingRelated(false);
    }
  };

  const runKeywordResearch = async (keyword: string) => {
    // Check if we need translations for non-English countries
    const nonEnglishCountries = selectedCountries.filter(code => {
      const country = COUNTRY_CONFIG.find(c => c.code === code);
      return country && !['UK', 'COM_US', 'CA', 'AU'].includes(code);
    });

    if (nonEnglishCountries.length > 0) {
      setPendingResearchKeyword(keyword);
      setShowTranslationDialog(true);
      return;
    }

    // Continue with research
    startResearchWithTranslations(keyword, {});
  };

  const getLanguageCode = (countryCode: string): string => {
    const languageMap: Record<string, string> = {
      'UK': 'en', 'COM_US': 'en', 'CA': 'en', 'AU': 'en',
      'NL': 'nl', 'FR': 'fr', 'DE': 'de', 'IT': 'it', 'ES': 'es',
      'SE': 'sv', 'NO': 'no', 'DK': 'da', 'FI': 'fi',
      'JP': 'ja', 'KR': 'ko', 'RU': 'ru', 'CN': 'zh',
      'COM_MX': 'es', 'COM_AR': 'es', 'COM_CL': 'es'
    };
    return languageMap[countryCode] || 'en';
  };

  const startResearchWithTranslations = async (keyword: string, translations: Record<string, string>) => {
    setResearchingKeyword(keyword);
    setResearchProgress('🚀 Starting research...');
    setShowTranslationDialog(false);
    
    try {
      const totalCountries = selectedCountries.length;
      let completedCountries = 0;

      // Store translated keywords
      for (const [countryCode, translatedKeyword] of Object.entries(translations)) {
        if (translatedKeyword && translatedKeyword.trim()) {
          const country = COUNTRY_CONFIG.find(c => c.code === countryCode);
          if (country) {
            // Check if this translated keyword already exists
            const { data: existing } = await (supabase as any)
              .from('project_keywords')
              .select('id')
              .eq('project_id', projectId)
              .eq('keyword', translatedKeyword)
              .eq('country_code', countryCode)
              .single();
            
            if (!existing) {
              await (supabase as any)
                .from('project_keywords')
                .insert([{
                  project_id: projectId,
                  keyword: translatedKeyword,
                  is_primary: false,
                  language_code: getLanguageCode(countryCode),
                  country_code: countryCode
                }]);
            }
          }
        }
      }

      // Check which countries have already been researched for this keyword via API
      const researchCheckResponse = await backendApiService.get(
        `/api/project-competitors/${projectId}/check`,
        { keyword }
      );
      const existingResearch = researchCheckResponse.success ? researchCheckResponse.data : [];

      const researchedCountries = new Set(
        existingResearch?.map((r: any) => r.research_country) || []
      );

      // Filter out already researched countries
      const countriesToResearch = selectedCountries.filter(
        country => !researchedCountries.has(country)
      );

      if (countriesToResearch.length === 0) {
        setResearchProgress('✅ All selected countries already researched for this keyword!');
        await new Promise(resolve => setTimeout(resolve, 2000));
        setResearchProgress('');
        return;
      }

      // Show which countries we're skipping
      if (researchedCountries.size > 0) {
        const skippedCountries = selectedCountries
          .filter(c => researchedCountries.has(c))
          .map(c => COUNTRY_CONFIG.find(cfg => cfg.code === c)?.name)
          .join(', ');
        
        setResearchProgress(`ℹ️ Skipping already researched: ${skippedCountries}`);
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      for (const countryCode of countriesToResearch) {
        const country = COUNTRY_CONFIG.find(c => c.code === countryCode);
        if (!country) continue;

        // Use translated keyword if available, otherwise use original
        const searchKeyword = translations[countryCode] || keyword;

        // Step 1: Get search volume
        setResearchProgress(`🔍 Finding search volume in ${country.flag} ${country.name} (${completedCountries + 1}/${totalCountries})...`);
        try {
          const volumeData = await backendApiService.getKeywordVolume(searchKeyword, countryCode);
          
          if (volumeData && volumeData.search_volume) {
            // Store volume with original keyword for consistency
            await (supabase as any)
              .from('project_keywords')
              .update({ search_volume: volumeData.search_volume })
              .eq('project_id', projectId)
              .eq('keyword', keyword);
          }
        } catch (error) {
          console.error(`Error getting volume for ${countryCode}`);
          setResearchProgress(`⚠️ Couldn't get search volume for ${country.flag} ${country.name}. Continuing...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Step 2: Get competitor rankings  
        setResearchProgress(`🏆 Analyzing competitors in ${country.flag} ${country.name} (${completedCountries + 1}/${totalCountries})...`);
        try {
          const rankingsData = await backendApiService.checkKeywordRankings(searchKeyword, countryCode);
          
          if (rankingsData && rankingsData.success && rankingsData.rankings) {
            const competitorInserts = rankingsData.rankings.slice(0, 10).map((result: any) => ({
              project_id: projectId,
              competitor_url: result.link,
              competitor_domain: new URL(result.link).hostname,
              ranking_position: result.position,
              title: result.title,
              meta_description: result.snippet || '',
              research_country: countryCode,
              website_id: country.website,
              source_keyword: keyword // Track which keyword generated this data
            }));

            if (competitorInserts.length > 0) {
              try {
                const insertResponse = await backendApiService.post('/api/project-competitors', {
                  competitors: competitorInserts
                });
                
                if (insertResponse.success) {
                  console.log(`Successfully saved ${insertResponse.data?.length || 0} competitors for ${countryCode}`);
                } else {
                  console.error('Error inserting competitors:', insertResponse.error);
                  setResearchProgress(`⚠️ Error saving competitors for ${country.flag} ${country.name}: ${insertResponse.message}`);
                  await new Promise(resolve => setTimeout(resolve, 2000));
                }
              } catch (insertError) {
                console.error('Error inserting competitors:', insertError);
                setResearchProgress(`⚠️ Error saving competitors for ${country.flag} ${country.name}`);
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }
          }
        } catch (error) {
          console.error(`Error getting rankings for ${countryCode}`);
          setResearchProgress(`⚠️ Couldn't analyze competitors for ${country.flag} ${country.name}. Continuing...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Step 3: Get PAA questions with selected depth (if not skipped)
        if (paaDepth > 0) {
          setResearchProgress(`🌳 Building PAA tree (${paaDepth} levels) in ${country.flag} ${country.name} (${completedCountries + 1}/${totalCountries})...`);
          try {
            const paaResponse = await backendApiService.post('/api/project-paa/research', {
              projectId: projectId,
              keyword: searchKeyword,
              country: countryCode,
              depth: paaDepth, // Use the selected depth
              sourceKeyword: keyword // Pass the original keyword for tracking
            }, 600000); // 10 minute timeout for PAA tree building
            
            if (paaResponse.success) {
              console.log(`Found ${paaResponse.data.total_questions} PAA questions for ${countryCode}`);
            }
          } catch (error) {
            console.error(`Error fetching PAA for ${countryCode}`);
            setResearchProgress(`⚠️ Couldn't build PAA tree for ${country.flag} ${country.name}. Continuing...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } else {
          setResearchProgress(`⏭️ Skipping PAA tree for ${country.flag} ${country.name} (${completedCountries + 1}/${totalCountries})...`);
        }

        completedCountries++;
      }

      // Step 4: Get related keywords (only once, for primary keyword)
      const isPrimary = keywords.find(k => k.keyword === keyword)?.is_primary;
      if (isPrimary && selectedCountries.length > 0) {
        setResearchProgress('💡 Discovering related keywords...');
        try {
          const firstCountryCode = selectedCountries[0];
          const firstCountryKeyword = translations[firstCountryCode] || keyword;
          const relatedResponse = await backendApiService.post('/api/keywords/ideas', {
            seedKeywords: [firstCountryKeyword],
            country: firstCountryCode, // Use first selected country
            language: 'en'
          });
          
          if (relatedResponse.keywords && relatedResponse.keywords.length > 0) {
            const existingKeywords = new Set(keywords.map(k => k.keyword.toLowerCase()));
            // Sort by search volume and take top 20
            const newKeywords = relatedResponse.keywords
              .filter((k: any) => !existingKeywords.has(k.keyword.toLowerCase()))
              .sort((a: any, b: any) => (b.avgMonthlySearches || 0) - (a.avgMonthlySearches || 0))
              .slice(0, 20);
            
            for (const kw of newKeywords) {
              await (supabase as any)
                .from('project_keywords')
                .insert([{
                  project_id: projectId,
                  keyword: kw.keyword,
                  search_volume: kw.avgMonthlySearches,
                  is_primary: false
                }]);
            }
          }
        } catch (error) {
          console.error('Error getting related keywords:', error);
        }
      }

      setResearchProgress('✨ Research complete!');
      
      // Check if confetti has been shown for this project
      const confettiKey = `project_${projectId}_confetti_shown`;
      const confettiShown = localStorage.getItem(confettiKey);
      
      if (!confettiShown && window.confetti) {
        // Show confetti for first successful research
        window.confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        localStorage.setItem(confettiKey, 'true');
      }
      
      setTimeout(() => setResearchProgress(''), 3000);
      fetchProjectData();
    } catch (error) {
      console.error('Error running research:', error);
      setResearchProgress('❌ Error during research. Please try again.');
      setTimeout(() => setResearchProgress(''), 5000);
    } finally {
      setResearchingKeyword(null);
    }
  };

  const fetchPAAQuestions = async () => {
    if (keywords.length === 0) return;
    
    setFetchingPAA(true);
    try {
      // Use primary keyword or first keyword
      const targetKeyword = keywords.find(k => k.is_primary)?.keyword || keywords[0].keyword;
      
      const response = await backendApiService.post('/api/project-paa/research', {
        projectId: projectId,
        keyword: targetKeyword,
        country: selectedCountries[0] || 'UK',
        depth: 2 // 2 levels deep
      });
      
      if (response.success) {
        console.log(`Found ${response.data.total_questions} PAA questions`);
        fetchProjectData(); // Refresh to show new FAQs
      }
    } catch (error) {
      console.error('Error fetching PAA questions:', error);
      alert('Error fetching People Also Ask questions. Please check the console.');
    } finally {
      setFetchingPAA(false);
    }
  };

  const publishProject = async () => {
    if (!publishUrl || !project) return;

    try {
      // First, check if URL already exists
      const { data: existingUrl } = await (supabase as any)
        .from('urls')
        .select('url_id')
        .eq('full_url', publishUrl)
        .single();

      let urlId = existingUrl?.url_id;

      if (!urlId) {
        // Create new URL entry
        const urlParts = new URL(publishUrl, 'https://example.com');
        urlId = `${project.website_id}_${urlParts.pathname}`.replace(/[^a-zA-Z0-9_-]/g, '_');
        
        await (supabase as any)
          .from('urls')
          .insert([{
            url_id: urlId,
            website_id: project.website_id,
            full_url: publishUrl,
            title: project.project_name
          }]);
      }

      // Migrate keywords to main keywords table
      for (const keyword of keywords) {
        // First check if keyword already exists for this URL
        const { data: existing } = await (supabase as any)
          .from('keywords')
          .select('id')
          .eq('url_id', urlId)
          .eq('keyword', keyword.keyword)
          .single();
        
        if (!existing) {
          await (supabase as any)
            .from('keywords')
            .insert([{
              url_id: urlId,
              keyword: keyword.keyword,
              is_primary: keyword.is_primary ? 1 : 0,
              search_volume: keyword.search_volume
            }]);
        }
      }

      // Update project status
      await (supabase as any)
        .from('content_projects')
        .update({
          status: 'published',
          published_at: new Date().toISOString(),
          published_url_id: urlId,
          target_url: publishUrl
        })
        .eq('id', projectId);

      // Clean up project research tables now that data is migrated
      await (supabase as any)
        .from('project_keywords')
        .delete()
        .eq('project_id', projectId);
      
      await backendApiService.delete(`/api/project-competitors/${projectId}`);
      
      await (supabase as any)
        .from('project_faq_research')
        .delete()
        .eq('project_id', projectId);

      alert('Project published successfully! Redirecting to URL management...');
      navigate('/url-management');
    } catch (error) {
      console.error('Error publishing project:', error);
      alert('Error publishing project. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Project not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton 
              onClick={() => navigate('/content-projects')}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" gutterBottom>
                {project.project_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Research Hub
              </Typography>
            </Box>
            <Chip
              label={project.status}
              color={project.status === 'published' ? 'success' : 'default'}
            />
          </Box>

          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              textColor="primary"
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              <Tab icon={<SearchIcon />} label="Keywords" iconPosition="start" />
              <Tab icon={<LanguageIcon />} label="Competitors" iconPosition="start" />
              <Tab icon={<QuestionAnswerIcon />} label="FAQ Research" iconPosition="start" />
              <Tab icon={<PublishIcon />} label="Publish" iconPosition="start" />
            </Tabs>
          </Paper>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddKeywordDialog(true)}
              >
                Add Keyword
              </Button>
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <InputLabel>Research Countries</InputLabel>
                <Select
                  multiple
                  value={selectedCountries}
                  onChange={(e) => setSelectedCountries(e.target.value as string[])}
                  label="Research Countries"
                  renderValue={(selected) => `${selected.length} countries selected`}
                >
                  {COUNTRY_CONFIG.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <input
                          type="checkbox"
                          checked={selectedCountries.includes(country.code)}
                          onChange={() => {}} // Handled by parent MenuItem onClick
                          style={{ marginRight: 8 }}
                        />
                        <span style={{ marginRight: 8 }}>{country.flag}</span>
                        <span>{country.name}</span>
                        {country.group && (
                          <Chip 
                            label={country.group} 
                            size="small" 
                            sx={{ ml: 'auto', height: 20 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>PAA Depth</InputLabel>
                <Select
                  value={paaDepth}
                  onChange={(e) => setPaaDepth(e.target.value as number)}
                  label="PAA Depth"
                >
                  <MenuItem value={0}>Skip PAA</MenuItem>
                  <MenuItem value={1}>1 Level</MenuItem>
                  <MenuItem value={2}>2 Levels</MenuItem>
                  <MenuItem value={3}>3 Levels</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {researchProgress && (
              <Alert 
                severity={researchProgress.includes('❌') ? 'error' : researchProgress.includes('complete') ? 'success' : 'info'} 
                sx={{ mb: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!researchProgress.includes('complete') && !researchProgress.includes('❌') && <CircularProgress size={20} />}
                  <Typography variant="body2">{researchProgress}</Typography>
                </Box>
              </Alert>
            )}

            {paaDepth > 2 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  ⚠️ PAA depth {paaDepth} can take 5-10 minutes per country. Consider using depth 2 or "Skip PAA" for faster research.
                </Typography>
              </Alert>
            )}

            {keywords.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No keywords yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Add keywords to start researching search volume and competition
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddKeywordDialog(true)}
                  >
                    Add First Keyword
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Box>
                {/* Group keywords by language/country */}
                {(() => {
                  const keywordGroups = keywords.reduce((groups, kw) => {
                    const groupKey = kw.language_code || kw.country_code || 'en';
                    if (!groups[groupKey]) groups[groupKey] = [];
                    groups[groupKey].push(kw);
                    return groups;
                  }, {} as Record<string, typeof keywords>);
                  
                  // Check if we have actual different languages (not just en/UK which are the same)
                  const uniqueLanguages = new Set(
                    keywords.map(kw => kw.language_code || 'en')
                  );
                  const hasMultipleLanguages = uniqueLanguages.size > 1;
                  const languageTabs = Object.keys(keywordGroups).sort();
                  
                  if (hasMultipleLanguages) {
                    return (
                      <Box>
                        <Tabs
                          value={languageTabValue}
                          onChange={(e, newValue) => setLanguageTabValue(newValue)}
                          sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            mb: 2,
                            '& .MuiTabs-indicator': {
                              backgroundColor: '#cc081c'
                            }
                          }}
                        >
                          {languageTabs.map((langCode, idx) => {
                            const country = langCode === 'en' ? 
                              { flag: '🇬🇧', name: 'English' } : 
                              COUNTRY_CONFIG.find(c => c.code === langCode);
                            return (
                              <Tab 
                                key={langCode}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{country?.flag || '🌐'}</span>
                                    <span>{country?.name || langCode}</span>
                                    <Chip size="small" label={keywordGroups[langCode].length} />
                                  </Box>
                                }
                              />
                            );
                          })}
                        </Tabs>
                        
                        {languageTabs.map((langCode, idx) => (
                          <Box key={langCode} hidden={languageTabValue !== idx}>
                            <List>
                              {keywordGroups[langCode].map((kw, index) => (
                                <React.Fragment key={kw.id}>
                                  {index > 0 && <Divider />}
                                  <ListItem sx={{ py: 2 }}>
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="subtitle1">{kw.keyword}</Typography>
                                          {kw.is_primary && (
                                            <Chip label="Primary" size="small" color="primary" />
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        kw.search_volume ? (
                                          <Typography variant="body2" color="text.secondary">
                                            Search Volume: {kw.search_volume.toLocaleString()}
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            No search volume data yet
                                          </Typography>
                                        )
                                      }
                                    />
                                    <ListItemSecondaryAction>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => runKeywordResearch(kw.keyword)}
                                        disabled={researchingKeyword === kw.keyword}
                                        startIcon={researchingKeyword === kw.keyword ? <CircularProgress size={16} /> : <ScienceIcon />}
                                        sx={{ 
                                          minWidth: 120,
                                          background: researchingKeyword === kw.keyword ? undefined : 'linear-gradient(45deg, #cc081c 30%, #9c88ff 90%)',
                                          '&:hover': {
                                            background: researchingKeyword === kw.keyword ? undefined : 'linear-gradient(45deg, #6c5ce7 30%, #a29bfe 90%)',
                                          }
                                        }}
                                      >
                                        {researchingKeyword === kw.keyword ? 'Researching...' : 'Research'}
                                      </Button>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                </React.Fragment>
                              ))}
                            </List>
                          </Box>
                        ))}
                      </Box>
                    );
                  } else {
                    // Single language - show simple list
                    return (
                      <List>
                        {keywords.map((kw, index) => (
                          <React.Fragment key={kw.id}>
                            {index > 0 && <Divider />}
                            <ListItem sx={{ py: 2 }}>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle1">{kw.keyword}</Typography>
                                    {kw.is_primary && (
                                      <Chip label="Primary" size="small" color="primary" />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  kw.search_volume ? (
                                    <Typography variant="body2" color="text.secondary">
                                      Search Volume: {kw.search_volume.toLocaleString()}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No search volume data yet
                                    </Typography>
                                  )
                                }
                              />
                              <ListItemSecondaryAction>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => runKeywordResearch(kw.keyword)}
                                  disabled={researchingKeyword === kw.keyword}
                                  startIcon={researchingKeyword === kw.keyword ? <CircularProgress size={16} /> : <ScienceIcon />}
                                  sx={{ 
                                    minWidth: 120,
                                    background: researchingKeyword === kw.keyword ? undefined : 'linear-gradient(45deg, #cc081c 30%, #9c88ff 90%)',
                                    '&:hover': {
                                      background: researchingKeyword === kw.keyword ? undefined : 'linear-gradient(45deg, #6c5ce7 30%, #a29bfe 90%)',
                                    }
                                  }}
                                >
                                  {researchingKeyword === kw.keyword ? 'Researching...' : 'Research'}
                                </Button>
                              </ListItemSecondaryAction>
                            </ListItem>
                          </React.Fragment>
                        ))}
                      </List>
                    );
                  }
                })()}
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {competitors.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <LanguageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No competitor analysis yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Run keyword research to see who's ranking for your target keywords
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Box>
                {/* Group competitors by country and source keyword */}
                {(() => {
                  const competitorGroups = competitors.reduce((groups, comp) => {
                    const country = comp.research_country || 'Unknown';
                    const sourceKeyword = (comp as any).source_keyword || 'Unknown Keyword';
                    const key = `${country}|${sourceKeyword}`;
                    if (!groups[key]) groups[key] = { country, sourceKeyword, competitors: [] };
                    groups[key].competitors.push(comp);
                    return groups;
                  }, {} as Record<string, { country: string, sourceKeyword: string, competitors: typeof competitors }>);
                  
                  // Get unique countries
                  const countries = Array.from(new Set(Object.values(competitorGroups).map(g => g.country))).sort();
                  const hasMultipleCountries = countries.length > 1;
                  
                  if (hasMultipleCountries) {
                    return (
                      <Box>
                        <Tabs
                          value={competitorTabValue}
                          onChange={(e, newValue) => setCompetitorTabValue(newValue)}
                          sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            mb: 2,
                            '& .MuiTabs-indicator': {
                              backgroundColor: '#cc081c'
                            }
                          }}
                        >
                          {countries.map((countryCode, idx) => {
                            const countryInfo = COUNTRY_CONFIG.find(c => c.code === countryCode);
                            const countryCompetitorCount = Object.values(competitorGroups)
                              .filter(g => g.country === countryCode)
                              .reduce((sum, g) => sum + g.competitors.length, 0);
                            return (
                              <Tab 
                                key={countryCode}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{countryInfo?.flag || '🌐'}</span>
                                    <span>{countryInfo?.name || countryCode}</span>
                                    <Chip size="small" label={countryCompetitorCount} />
                                  </Box>
                                }
                              />
                            );
                          })}
                        </Tabs>
                        
                        {countries.map((countryCode, idx) => (
                          <Box key={countryCode} hidden={competitorTabValue !== idx}>
                            {Object.entries(competitorGroups)
                              .filter(([_, group]) => group.country === countryCode)
                              .map(([key, group]) => (
                                <Box key={key} sx={{ mb: 3 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Typography variant="subtitle1">From:</Typography>
                                    <Chip 
                                      label={group.sourceKeyword} 
                                      size="small" 
                                      color="primary"
                                      variant="outlined"
                                      icon={<SearchIcon />}
                                    />
                                    <Chip 
                                      label={COUNTRY_CONFIG.find(c => c.code === countryCode)?.website || 'Unknown website'} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  </Box>
                                  <Grid container spacing={2}>
                                    {group.competitors.map((comp) => (
                                <Grid size={{ xs: 12 }} key={comp.id}>
                                  <Card>
                                    <CardContent>
                                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                        <Chip
                                          label={`#${comp.ranking_position}`}
                                          color="primary"
                                          sx={{ fontWeight: 'bold' }}
                                        />
                                        <Box sx={{ flexGrow: 1 }}>
                                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {comp.title}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="primary"
                                            component="a"
                                            href={comp.competitor_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ textDecoration: 'none' }}
                                          >
                                            {comp.competitor_domain}
                                          </Typography>
                                          {comp.meta_description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                              {comp.meta_description}
                                            </Typography>
                                          )}
                                        </Box>
                                        {comp.word_count && (
                                          <Chip
                                            label={`${comp.word_count} words`}
                                            size="small"
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                    ))}
                                  </Grid>
                                </Box>
                              ))}
                          </Box>
                        ))}
                      </Box>
                    );
                  } else {
                    // Single country - show simple display
                    return (
                      <Box>
                        {Object.entries(competitorGroups).map(([key, group]) => {
                          const countryInfo = COUNTRY_CONFIG.find(c => c.code === group.country);
                          
                          return (
                            <Box key={key} sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography variant="h6">
                                  {countryInfo?.flag} {countryInfo?.name || group.country}
                                </Typography>
                                <Typography variant="subtitle1">From:</Typography>
                                <Chip 
                                  label={group.sourceKeyword} 
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                  icon={<SearchIcon />}
                                />
                                <Chip 
                                  label={countryInfo?.website || 'Unknown website'} 
                                  size="small" 
                                  variant="outlined"
                                />
                                <Typography variant="body2" color="text.secondary">
                                  ({group.competitors.length} competitors)
                                </Typography>
                              </Box>
                              <Grid container spacing={2}>
                                {group.competitors.map((comp) => (
                            <Grid size={{ xs: 12 }} key={comp.id}>
                              <Card>
                                <CardContent>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Chip
                                      label={`#${comp.ranking_position}`}
                                      color="primary"
                                      sx={{ fontWeight: 'bold' }}
                                    />
                                    <Box sx={{ flexGrow: 1 }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {comp.title}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="primary"
                                        component="a"
                                        href={comp.competitor_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ textDecoration: 'none' }}
                                      >
                                        {comp.competitor_domain}
                                      </Typography>
                                      {comp.meta_description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                          {comp.meta_description}
                                        </Typography>
                                      )}
                                    </Box>
                                    {comp.word_count && (
                                      <Chip
                                        label={`${comp.word_count} words`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                          );
                        })}
                      </Box>
                    );
                  }
                })()}
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {keywords.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <QuestionAnswerIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Add Keywords First
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add keywords to your project to research People Also Ask questions
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Box>
                {faqs.length === 0 && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      No PAA questions yet
                    </Typography>
                    <Typography variant="body2">
                      Click the "Research" button on any keyword in the Keywords tab to fetch People Also Ask questions.
                      The magical Research button will get search volume, competitors, related keywords (for primary), and PAA questions all at once!
                    </Typography>
                  </Alert>
                )}
                
                {faqs.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Total questions found: {faqs.length}
                    </Typography>
                    
                    {/* Group FAQs by source keyword */}
                    {Object.entries(
                      faqs.reduce((groups, faq) => {
                        const sourceKeyword = (faq as any).source_keyword || 'Unknown Source';
                        if (!groups[sourceKeyword]) {
                          groups[sourceKeyword] = [];
                        }
                        groups[sourceKeyword].push(faq);
                        return groups;
                      }, {} as Record<string, FAQResearch[]>)
                    ).map(([sourceKeyword, keywordFaqs]) => (
                      <Box key={sourceKeyword} sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            From Keyword:
                          </Typography>
                          <Chip 
                            label={sourceKeyword} 
                            color="primary" 
                            variant="outlined"
                            icon={<SearchIcon />}
                          />
                          <Typography variant="body2" color="text.secondary">
                            ({keywordFaqs.length} questions)
                          </Typography>
                        </Box>
                        
                        {/* Display all questions in a flat list, sorted by depth */}
                        <Box sx={{ pl: 0 }}>
                          {keywordFaqs
                            .sort((a, b) => (a.depth_level || 0) - (b.depth_level || 0))
                            .map((faq) => {
                              const depthLevel = faq.depth_level || 0;
                              const isRoot = depthLevel === 0;
                              
                              return (
                                <Card 
                                  key={faq.id} 
                                  sx={{ 
                                    mb: 2, 
                                    ml: depthLevel * 4, // Indent based on depth
                                    bgcolor: isRoot ? '#f3f0ff' : depthLevel === 1 ? '#fff7ed' : '#e8f5e9',
                                    border: isRoot ? '2px solid #cc081c' : depthLevel === 1 ? '1px solid #ff9500' : '1px solid #4caf50'
                                  }}
                                >
                                  <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                      {depthLevel > 0 && (
                                        <Chip 
                                          label={`L${depthLevel}`} 
                                          size="small" 
                                          sx={{ 
                                            minWidth: 32,
                                            height: 20,
                                            bgcolor: depthLevel === 1 ? '#ff9500' : '#4caf50',
                                            color: 'white',
                                            fontSize: '0.7rem'
                                          }}
                                        />
                                      )}
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                          {faq.question}
                                        </Typography>
                                        {faq.answer && (
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {faq.answer}
                                          </Typography>
                                        )}
                                        {faq.source_url && (
                                          <Typography 
                                            variant="caption" 
                                            component="a"
                                            href={faq.source_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: 'primary.main', textDecoration: 'none' }}
                                          >
                                            View source →
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </CardContent>
                                </Card>
                              );
                            })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Publish Content
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Once your content is live, enter the URL below to transition this project to live tracking.
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Published URL"
                      value={publishUrl}
                      onChange={(e) => setPublishUrl(e.target.value)}
                      placeholder="https://example.com/blog/my-new-post"
                      sx={{ mb: 3 }}
                    />

                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        What happens when you publish:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        <li>All keyword research transfers to the URL</li>
                        <li>Rankings tracking begins automatically</li>
                        <li>Project status changes to "Published"</li>
                        <li>You can still access historical research data</li>
                      </ul>
                    </Alert>

                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      fullWidth
                      onClick={publishProject}
                      disabled={!publishUrl || project.status === 'published'}
                      startIcon={<PublishIcon />}
                    >
                      {project.status === 'published' ? 'Already Published' : 'Publish & Start Tracking'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Grid>
      </Grid>

      {/* Add Keyword Dialog */}
      <Dialog open={showAddKeywordDialog} onClose={() => setShowAddKeywordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Keyword</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Keyword"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Enter keyword..."
            sx={{ 
              mt: 2,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#ccc',
                }
              }
            }}
            autoFocus
            onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddKeywordDialog(false)}>Cancel</Button>
          <Button onClick={addKeyword} variant="contained" disabled={!newKeyword.trim()}>
            Add Keyword
          </Button>
        </DialogActions>
      </Dialog>

      {/* Translation Dialog */}
      <Dialog open={showTranslationDialog} onClose={() => setShowTranslationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Translate Keyword for Different Languages</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Please provide translations for <strong>"{pendingResearchKeyword}"</strong> in the languages below.
              You can skip English-speaking countries (UK, US, Canada, Australia).
            </Typography>
          </Alert>
          
          {selectedCountries.filter(code => {
            const country = COUNTRY_CONFIG.find(c => c.code === code);
            return country && !['UK', 'COM_US', 'CA', 'AU'].includes(code);
          }).map((countryCode) => {
            const country = COUNTRY_CONFIG.find(c => c.code === countryCode);
            if (!country) return null;
            
            return (
              <Box key={countryCode} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label={`${country.flag} ${country.name} (${country.website})`}
                  value={keywordTranslations[countryCode] || ''}
                  onChange={(e) => setKeywordTranslations({
                    ...keywordTranslations,
                    [countryCode]: e.target.value
                  })}
                  placeholder={`Translation for ${country.name}...`}
                  helperText={countryCode.startsWith('COM') ? 'Spanish translation' : `Translation for ${country.name}`}
                />
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowTranslationDialog(false);
            setKeywordTranslations({});
          }}>
            Cancel
          </Button>
          <Button 
            onClick={() => startResearchWithTranslations(pendingResearchKeyword, keywordTranslations)} 
            variant="contained"
          >
            Start Research
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};